import type { LucideIcon } from 'lucide-react';
import { Grip, Hand, Lightbulb, ListOrdered, Share2, Star, Target, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BoardDashboardClimb } from '../../../types/board-dashboard';

interface StatChipProps {
  icon: LucideIcon;
  value: string | number;
  /** Read out and shown on hover — the chips themselves are icon + number. */
  label: string;
  /** Dims a chip whose number is zero, so a busy problem stands out at a glance. */
  muted?: boolean;
  className?: string;
}

/**
 * One number about a problem. Icon and figure only: a dashboard row carries
 * seven of these, and spelling each one out would make the list unreadable.
 */
export function StatChip({ icon: Icon, value, label, muted = false, className }: StatChipProps) {
  return (
    <span
      title={label}
      aria-label={`${label}: ${value}`}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap text-xs tabular-nums',
        muted ? 'text-muted-foreground/50' : 'text-muted-foreground',
        className
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      {value}
    </span>
  );
}

interface ClimbStatChipsProps {
  climb: BoardDashboardClimb;
  labels: {
    sends: string;
    attempts: string;
    holds: string;
    rating: string;
    lightings: string;
    shares: string;
    circuit: string;
    forcedHands: string;
  };
}

/**
 * The full set, in the order a setter reads them: how many got up it, how hard
 * they worked for it, how big it is, what they thought of it, and how much the
 * gym has seen of it.
 */
export function ClimbStatChips({ climb, labels }: ClimbStatChipsProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <StatChip icon={Trophy} value={climb.sends_count} label={labels.sends} muted={climb.sends_count === 0} />
      <StatChip
        icon={Target}
        value={climb.attempts_count}
        label={labels.attempts}
        muted={climb.attempts_count === 0}
      />
      <StatChip icon={Grip} value={climb.holds_count} label={labels.holds} />
      <StatChip
        icon={Star}
        // An average of nothing is not zero stars, it is no opinion yet.
        value={climb.rating_avg === null ? '—' : climb.rating_avg.toFixed(1)}
        label={labels.rating}
        muted={climb.rating_avg === null}
      />
      <StatChip
        icon={Lightbulb}
        value={climb.lightings_count}
        label={labels.lightings}
        muted={climb.lightings_count === 0}
      />
      <StatChip
        icon={Share2}
        value={climb.shares_count}
        label={labels.shares}
        muted={climb.shares_count === 0}
      />
      {climb.circuit && (
        <StatChip icon={ListOrdered} value="" label={labels.circuit} className="text-primary" />
      )}
      {/* Only ever on a circuit, so it reads as a qualifier on the chip beside
          it rather than as a mode of its own. */}
      {climb.forced_hands && (
        <StatChip icon={Hand} value="" label={labels.forcedHands} className="text-primary" />
      )}
    </div>
  );
}
